import { VisitStatusKind, getVisitStatusKind } from '@/components/visits/visitStatusGlyph';
import { VisitOutcome, VisitState } from '@/types/graphql';
import {
    CancelOutlined,
    CheckCircleOutline,
    EventAvailable,
    EventBusy,
    PendingOutlined,
    PersonRemove,
} from '@mui/icons-material';

import { CalendarEventClinician, ServicesForCardDisplay } from './types';

export enum EventStatus {
    NEEDS_ASSIGNMENT = 'NEEDS_ASSIGNMENT',
    NEEDS_TIME = 'NEEDS_TIME',
    SCHEDULED = 'SCHEDULED',
    COMPLETED = 'COMPLETED',
    MISSED = 'MISSED',
    CANCELED = 'CANCELED',
}

export const getStatusIcon = (eventStatus: EventStatus, color = '#1D1B20') => {
    const iconProps = { sx: { fontSize: '18px', color } };
    switch (eventStatus) {
        case EventStatus.NEEDS_ASSIGNMENT:
            return <PersonRemove {...iconProps} />;
        case EventStatus.NEEDS_TIME:
            return <PendingOutlined {...iconProps} />;
        case EventStatus.SCHEDULED:
            return <EventAvailable {...iconProps} />;
        case EventStatus.COMPLETED:
            return <CheckCircleOutline {...iconProps} />;
        case EventStatus.MISSED:
            return <CancelOutlined {...iconProps} />;
        case EventStatus.CANCELED:
            return <EventBusy {...iconProps} />;
        default:
            return <EventAvailable {...iconProps} />;
    }
};

const SCHEDULED_OR_IN_PROGRESS_STATES: VisitState[] = [
    VisitState.SCHEDULED,
    VisitState.CONFIRMED,
    VisitState.EN_ROUTE,
    VisitState.AT_LOCATION,
];

/**
 * Determine EventStatus for a visit based on state, outcome, and appointment data.
 *
 * Terminal status (completed/missed/canceled) comes from the shared getVisitStatusKind so the
 * calendar, list, and rail never disagree. The calendar-only workflow states layer on top:
 * - COMPLETED / MISSED / CANCELED: from getVisitStatusKind
 * - COMPLETED: also when a visit is over (ENDED/IN_REVIEW) with no recorded outcome
 * - NEEDS_TIME: UNSCHEDULED with service, clinician, date, but no time
 * - SCHEDULED: SCHEDULED, CONFIRMED, EN_ROUTE, or AT_LOCATION with service, clinician, date, and time
 * - NEEDS_ASSIGNMENT: default fallback
 */
export const getVisitEventStatus = (
    visitState: VisitState,
    visitOutcome: VisitOutcome,
    appointment: {
        services: ServicesForCardDisplay[];
        clinician: CalendarEventClinician | null;
        startDatetime: Date | null;
        endDatetime: Date | null;
    },
): EventStatus => {
    const hasService = appointment.services.length > 0;
    const hasClinician = appointment.clinician !== null;
    const hasTime = appointment.startDatetime !== null && appointment.endDatetime !== null;

    const kind = getVisitStatusKind(visitState, visitOutcome);
    if (kind === VisitStatusKind.Canceled) {
        return EventStatus.CANCELED;
    }
    if (kind === VisitStatusKind.Missed) {
        return EventStatus.MISSED;
    }
    if (kind === VisitStatusKind.Completed) {
        return EventStatus.COMPLETED;
    }

    // No terminal outcome. A visit that is over reads as completed; otherwise it is still
    // pending and the calendar reports its own workflow granularity.
    if (visitState === VisitState.ENDED || visitState === VisitState.IN_REVIEW) {
        return EventStatus.COMPLETED;
    }
    if (visitState === VisitState.UNSCHEDULED && hasService && hasClinician && !hasTime) {
        return EventStatus.NEEDS_TIME;
    }
    if (
        SCHEDULED_OR_IN_PROGRESS_STATES.includes(visitState) &&
        hasService &&
        hasClinician &&
        hasTime
    ) {
        return EventStatus.SCHEDULED;
    }
    return EventStatus.NEEDS_ASSIGNMENT;
};

export const readableEventStatus = (status: EventStatus) =>
    status
        .toLowerCase()
        .split('_')
        .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
        .join(' ');
